<template>
  <div>
    <Toolbar/>

    <div class="container-xxl">
      <div class="card mb-5">
        <div class="card-header border-0 pt-6">
          <div class="card-title">
            <h3 class="fw-bold m-0">Lotes de Guias</h3>
          </div>
          <div class="card-toolbar">
            <router-link to="/tiss/guides" class="btn btn-light-primary me-2">
              <i class="bi bi-arrow-left"></i> Voltar para Guias
            </router-link>
          </div>
        </div>

        <div class="card-body pt-0">
          <!-- Filtros -->
          <div class="card mb-5 mb-xl-8">
            <div class="card-header border-0 pt-5">
              <h3 class="card-title align-items-start flex-column">
                <span class="card-label fw-bold fs-3 mb-1">Filtros</span>
              </h3>
            </div>
            <div class="card-body pt-0">
              <div class="row g-3">
                <div class="col-md-2">
                  <label class="form-label">Status</label>
                  <select v-model="filters.status" class="form-select">
                    <option value="">Todos</option>
                    <option value="draft">Rascunho</option>
                    <option value="closed">Fechado</option>
                    <option value="sent">Enviado</option>
                    <option value="processed">Processado</option>
                    <option value="cancelled">Cancelado</option>
                  </select>
                </div>
                <div class="col-md-2">
                  <label class="form-label">Tipo</label>
                  <select v-model="filters.batch_type" class="form-select">
                    <option value="">Todos</option>
                    <option value="tiss">Recebimento (TISS)</option>
                    <option value="payment">Pagamento</option>
                  </select>
                </div>
                <div class="col-md-2">
                  <label class="form-label">Profissional</label>
                  <select v-model="filters.id_prof" class="form-select">
                    <option value="">Todos</option>
                    <option v-for="prof in professionals" :key="prof.id_prof" :value="prof.id_prof">
                      {{ prof.prof_name }}
                    </option>
                  </select>
                </div>
                <div class="col-md-2">
                  <label class="form-label">Convênio</label>
                  <select v-model="filters.id_insurance" class="form-select">
                    <option value="">Todos</option>
                    <option v-for="insurance in insurances" :key="insurance.id_insurance" :value="insurance.id_insurance">
                      {{ insurance.insurance_name }}
                    </option>
                  </select>
                </div>
                <div class="col-md-2">
                  <label class="form-label">Data Inicial</label>
                  <input v-model="filters.start_date" type="date" class="form-control">
                </div>
                <div class="col-md-2">
                  <label class="form-label">Data Final</label>
                  <input v-model="filters.end_date" type="date" class="form-control">
                </div>
              </div>
              <div class="row g-3 mt-2">
                <div class="col-md-4">
                  <label class="form-label">Buscar</label>
                  <input v-model="filters.search" class="form-control" placeholder="Número do lote...">
                </div>
                <div class="col-md-2 d-flex align-items-end">
                  <button @click="fetchBatches" class="btn btn-primary w-100">
                    <i class="bi bi-search"></i> Filtrar
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Tabela -->
          <div class="table-responsive">
            <table class="table table-row-bordered table-row-gray-300 align-middle gs-0 gy-4">
              <thead>
                <tr class="fw-bold text-muted">
                  <th class="min-w-150px">Número do Lote</th>
                  <th class="min-w-100px">Tipo</th>
                  <th class="min-w-100px">Status</th>
                  <th class="min-w-100px">Total de Itens</th>
                  <th class="min-w-100px">Valor Total</th>
                  <th class="min-w-150px">Data Criação</th>
                  <th class="min-w-150px text-end">Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="batch in batches.data" :key="batch.id">
                  <td>
                    <span class="text-dark fw-bold d-block fs-6">{{ batch.batch_number }}</span>
                  </td>
                  <td>
                    <span :class="batchTypeClass(batch.batch_type)">
                      {{ batchTypeLabel(batch.batch_type) }}
                    </span>
                  </td>
                  <td>
                    <span :class="statusClass(batch.status)">
                      {{ statusLabel(batch.status) }}
                    </span>
                  </td>
                  <td>
                    <span class="text-dark fw-semibold d-block fs-7">
                      {{ batch.total_guides }}
                      <small v-if="batch.batch_type === 'payment' && batch.items" class="text-muted">
                        (+ {{ batch.items.filter(item => item.item_type === 'manual').length }} avulsos)
                      </small>
                    </span>
                  </td>
                  <td>
                    <span class="text-dark fw-bold d-block fs-6">
                      R$ {{ formatCurrency(batch.total_amount) }}
                    </span>
                  </td>
                  <td>
                    <span class="text-muted fw-semibold d-block fs-7">
                      {{ formatDate(batch.created_at) }}
                    </span>
                  </td>
                  <td class="text-end">
                    <button v-if="canReopenBatch(batch) && hasPermission('Reopen TISS Batch')"
                            class="btn btn-icon btn-bg-light btn-active-color-warning btn-sm me-2"
                            @click="openReopenModal(batch)"
                            :disabled="loading"
                            title="Reabrir Lote">
                      <i class="bi bi-arrow-counterclockwise"></i>
                    </button>
                    <button v-if="batch.status === 'draft'"
                            class="btn btn-icon btn-bg-light btn-active-color-success btn-sm me-2"
                            @click="closeBatch(batch)"
                            :disabled="loading"
                            title="Fechar Lote">
                      <i class="bi bi-lock"></i>
                    </button>
                    <button v-if="hasPermission('Store TISS Batch')"
                            class="btn btn-icon btn-bg-light btn-active-color-danger btn-sm me-2"
                            @click="confirmDelete(batch)"
                            :disabled="loading"
                            title="Excluir Lote">
                      <i class="bi bi-trash"></i>
                    </button>
                    <button class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-2"
                            @click="openDetailModal(batch)"
                            title="Ver Detalhes">
                      <i class="bi bi-eye"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="!batches.data || batches.data.length === 0">
                  <td colspan="7" class="text-center text-muted py-10">
                    <i class="bi bi-inbox fs-1"></i>
                    <div class="mt-3">Nenhum lote encontrado</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <Pagination :data="batches" v-if="batches.data && batches.data.length > 0" />
        </div>
      </div>
    </div>

    <TissBatchDetailModal
      :show="showDetailModal"
      :batch="selectedBatch"
      @close="closeDetailModal"
      @updated="fetchBatches"
      @update:batch="selectedBatch = $event"/>

    <!-- Modal Reabrir Lote -->
    <ModalTissBatchReopen
      v-if="selectedBatchForReopen"
      :show="showReopenModal"
      :batch="selectedBatchForReopen"
      @close="closeReopenModal"
      @reopened="onBatchReopened($event)"/>
  </div>
</template>

<script>
import http from "@/helpers/http";
import Toolbar from "@/components/layouts/auth/Toolbar.vue";
import Pagination from "@/components/Pagination.vue";
import TissBatchDetailModal from "./TissBatchDetailModal.vue";
import ModalTissBatchReopen from "./ModalTissBatchReopen.vue";
import Alert from "@/helpers/alert";
import Loader from "@/helpers/loader";
import Permission from "@/helpers/permission";

export default {
  name: "TissBatches",
  components: {
    Toolbar,
    Pagination,
    TissBatchDetailModal,
    ModalTissBatchReopen,
  },
  data() {
    return {
      batches: {
        data: [],
        current_page: 1,
        per_page: 15,
        total: 0,
        last_page: 1,
      },
      filters: {
        status: '',
        batch_type: '',
        id_prof: '',
        id_insurance: '',
        start_date: '',
        end_date: '',
        search: '',
      },
      professionals: [],
      insurances: [],
      showDetailModal: false,
      selectedBatch: null,
      showReopenModal: false,
      selectedBatchForReopen: null,
      loading: false,
    };
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
  },
  mounted() {
    this.loadProfessionals();
    this.loadInsurances();
    this.fetchBatches();
  },
  methods: {
    batchId(batch) {
      return batch?.id ?? batch?.id_tiss_batch;
    },
    apiError(error, fallback) {
      const data = error.response?.data;
      if (typeof data === 'string' && data.trim()) {
        return data;
      }
      return data?.error || data?.message || fallback;
    },
    fetchBatches() {
      const params = {
        page: this.batches.current_page,
      };

      if (this.filters.status) {
        params.status = this.filters.status;
      }
      if (this.filters.batch_type) {
        params.batch_type = this.filters.batch_type;
      }
      if (this.filters.start_date) {
        params.start_date = this.filters.start_date;
      }
      if (this.filters.end_date) {
        params.end_date = this.filters.end_date;
      }
      if (this.filters.search) {
        params.search = this.filters.search;
      }
      if (this.filters.id_prof) {
        params.id_prof = this.filters.id_prof;
      }
      if (this.filters.id_insurance) {
        params.id_insurance = this.filters.id_insurance;
      }

      http.get(`/places/${this.place.id_place}/tiss-batches`, { params })
        .then((response) => {
          this.batches = response.data;
        })
        .catch((error) => {
          Alert.error('Erro ao carregar lotes');
          console.error(error);
        });
    },
    openDetailModal(batch) {
      this.selectedBatch = batch;
      this.showDetailModal = true;
    },
    closeDetailModal() {
      this.showDetailModal = false;
      this.selectedBatch = null;
    },
    closeBatch(batch) {
      Swal.fire({
        title: "Tem certeza?",
        text: "Tem certeza que deseja fechar este lote? As contas serão geradas automaticamente.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sim, fechar lote",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33"
      }).then((result) => {
        if (result.isConfirmed) {
          this.loading = true;
          Loader.show('Fechando lote e gerando contas...');

          http.post(`/places/${this.place.id_place}/tiss-batches/${this.batchId(batch)}/close`)
            .then((response) => {
              const billsCount = response.data?.bills_count || 0;
              const billKind = batch.batch_type === 'payment' ? 'a pagar' : 'a receber';
              const message = billsCount > 0
                ? `Lote fechado e ${billsCount} conta(s) ${billKind} gerada(s)!`
                : 'Lote fechado, mas nenhuma conta foi gerada.';
              if (billsCount > 0) {
                Alert.success(message);
              } else {
                Alert.error(message);
              }
              this.fetchBatches();
            })
            .catch((error) => {
              Alert.error(this.apiError(error, 'Erro ao fechar lote'));
              console.error(error);
            })
            .finally(() => {
              this.loading = false;
              Loader.close();
            });
        }
      });
    },
    canReopenBatch(batch) {
      // Só permite reabrir lotes fechados ou enviados
      return ['closed', 'sent'].includes(batch.status);
    },
    confirmDelete(batch) {
      const billsHint = batch.batch_type === 'payment'
        ? 'As contas a pagar geradas por este lote também serão excluídas.'
        : 'As contas a receber geradas por este lote também serão excluídas.';
      Swal.fire({
        title: "Excluir lote?",
        text: `Tem certeza que deseja excluir o lote ${batch.batch_number}? ${billsHint} As guias serão desvinculadas e poderão ser incluídas em outro lote.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sim, excluir",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#d33",
        cancelButtonColor: "#6c757d"
      }).then((result) => {
        if (result.isConfirmed) {
          this.loading = true;
          Loader.show('Excluindo lote...');
          http.delete(`/places/${this.place.id_place}/tiss-batches/${this.batchId(batch)}`)
            .then((response) => {
              Alert.success(response.data?.message || 'Lote excluído com sucesso!');
              this.fetchBatches();
            })
            .catch((error) => {
              Alert.error(error.response?.data?.error || error.response?.data?.message || 'Erro ao excluir lote');
              console.error(error);
            })
            .finally(() => {
              this.loading = false;
              Loader.close();
            });
        }
      });
    },
    openReopenModal(batch) {
      this.selectedBatchForReopen = batch;
      this.showReopenModal = true;
    },
    closeReopenModal() {
      this.showReopenModal = false;
      this.selectedBatchForReopen = null;
    },
    onBatchReopened(payload) {
      this.fetchBatches();
      if (payload?.batch && this.selectedBatch?.id === payload.batch.id) {
        Object.assign(this.selectedBatch, payload.batch);
      }
    },
    statusClass(status) {
      const classes = {
        draft: 'badge badge-secondary',
        closed: 'badge badge-primary',
        sent: 'badge badge-info',
        processed: 'badge badge-success',
        cancelled: 'badge badge-danger',
      };
      return classes[status] || 'badge badge-secondary';
    },
    statusLabel(status) {
      const labels = {
        draft: 'Rascunho',
        closed: 'Fechado',
        sent: 'Enviado',
        processed: 'Processado',
        cancelled: 'Cancelado',
      };
      return labels[status] || status;
    },
    batchTypeClass(type) {
      const classes = {
        tiss: 'badge badge-info',
        payment: 'badge badge-warning',
      };
      return classes[type] || 'badge badge-secondary';
    },
    batchTypeLabel(type) {
      const labels = {
        tiss: 'Recebimento',
        payment: 'Pagamento',
      };
      return labels[type] || type;
    },
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('pt-BR');
    },
    formatCurrency(value) {
      return parseFloat(value || 0).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    loadProfessionals() {
      this.professionals = this.$store.state.auth.professionals || [];
    },
    loadInsurances() {
      if (!this.place?.id_place) return;
      http.get(`/places/${this.place.id_place}/insurances`)
        .then((response) => {
          this.insurances = response.data || [];
        })
        .catch((error) => {
          console.error('Erro ao carregar convênios:', error);
        });
    },
    hasPermission(permission) {
      return Permission.has(permission);
    },
  },
};
</script>

<style scoped>
</style>

